import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';

import { neutral } from '../../../styles/mui/colors';
import useScreenSize from '../../../util/hooks/useScreenSize';
import useStoryblokImage from '../../../util/hooks/useStoryblokImage';
import ProgressiveImage from '../../Image/ProgressiveImage';
import Link from '../../Links/Link/Link';

const linkStyle = {
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none',
  },
};

const cardStyle = {
  width: '100%',
  minHeight: { xs: 'auto', md: 360 },
  p: 2,
  boxShadow: 0,
  border: `1px solid ${neutral[200]}`,
  borderRadius: 2,
  display: 'flex',
  flexDirection: { xs: 'row', md: 'column' },
  '&:hover': {
    boxShadow: 1,
  },
};

const cardMediaStyle = {
  borderRadius: 1,
  overflow: 'hidden',
  // On mobile: image is on the left, so we make it a fixed width.
  // On desktop: full width above the content.
  width: { xs: '96px', md: '100%' },
  height: { xs: '96px', md: 180 },
  flexShrink: 0,
};

const cardContentStyle = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '&:last-child': { px: 0, pt: 0 },
  // On mobile: add margin-left since image is to the left
  ml: { xs: 2, md: 0 },
  mt: { xs: 0, md: 2 },
  flex: 1,
};

/**
 * DestinationCardComponent
 *
 * Displays a card with an image, title, and optional subtitle.
 * On desktop, it appears with the image on top and content below.
 * On mobile, the layout adjusts so the image is on the left and
 * the content is on the right.
 */
const DestinationCardComponent = ({ title, subtitle = '', image, link }) => {
  const { filename, alt, title: imageTitle } = image;
  const { sources, src, blurSrc } = useStoryblokImage(filename, {
    size: 320,
  });

  const { isMobile } = useScreenSize();

  return (
    <Link sx={linkStyle} to={link} underline="none" width="100%">
      <Card sx={cardStyle}>
        <CardMedia sx={cardMediaStyle}>
          <ProgressiveImage
            alt={alt}
            blurSrc={blurSrc}
            height={isMobile ? '96px' : '120px'}
            sources={sources}
            src={src}
            title={imageTitle}
          />
        </CardMedia>
        <CardContent sx={cardContentStyle}>
          <Typography component="h3" gutterBottom variant="h5">
            {title}
          </Typography>
          <Typography color="text.secondary" component="p" variant="subtitle1">
            {subtitle}
          </Typography>
        </CardContent>
      </Card>
    </Link>
  );
};

DestinationCardComponent.displayName = 'DestinationCard';
const DestinationCard = memo(DestinationCardComponent);
export default DestinationCard;
